import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  TextField,
  CircularProgress,
  Tooltip,
  Paper,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSimulation } from '../../Contexts/SimulationContext';
import { useAuth } from '../../Contexts/AuthContext';
import { getOrganizations } from '../../Helpers/dataApi';

const ROLE_NAMES = {
  0: 'Super User',
  1: 'Admin',
  2: 'Contributor',
  3: 'Read-only',
};

const SimulationControls = () => {
  const auth = useAuth();
  const simulation = useSimulation();
  
  // Staged values (pending changes) – initialized from current simulation state to
  // avoid an out-of-range Select value and spurious hasPendingChanges=true on first render
  const [stagedRole, setStagedRole] = useState(simulation.actualRole);
  const [stagedOrganization, setStagedOrganization] = useState(simulation.actualOrganization);
  
  // Organization dropdown state
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  
  // Track if there are pending changes
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const isSuperUser = auth.activeUser && auth.activeUser.userType === 0;

  const effectiveRole = simulation.getEffectiveRole();
  const effectiveOrg = simulation.getEffectiveOrganization();
  const isSimulating = simulation.isSimulating;

  // Initialize staged values from current simulation state
  useEffect(() => {
    if (!isSuperUser) return;
    setStagedRole(simulation.simulatedRole !== null ? simulation.simulatedRole : simulation.actualRole);
    setStagedOrganization(simulation.simulatedOrganization || simulation.actualOrganization);
  }, [isSuperUser, simulation.simulatedRole, simulation.simulatedOrganization, simulation.actualRole, simulation.actualOrganization]);

  // Check if there are pending changes
  useEffect(() => {
    const roleChanged = stagedRole !== effectiveRole;
    const orgChanged = stagedOrganization?.id !== effectiveOrg?.id;
    setHasPendingChanges(roleChanged || orgChanged);
  }, [stagedRole, stagedOrganization, effectiveRole, effectiveOrg]);

  // Load organizations when dropdown opens
  useEffect(() => {
    let active = true;

    if (!loadingOrgs) {
      return undefined;
    }

    (async () => {
      try {
        const response = await getOrganizations(auth.activeUser?.accessToken);
        if (active && response.status === 200) {
          const orgs = response.data.map(o => ({
            name: o.name,
            id: o.id,
          }));
          setOrganizations(orgs);
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        if (active) {
          setLoadingOrgs(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [loadingOrgs, auth.activeUser?.accessToken]);

  // Clear organizations when dropdown closes
  useEffect(() => {
    if (!orgDropdownOpen) {
      setOrganizations([]);
    }
  }, [orgDropdownOpen]);

  // Only show for Super Users - guard after all hooks
  if (!isSuperUser) {
    return null;
  }

  const handleRoleChange = (event) => {
    setStagedRole(event.target.value);
  };

  const handleOrgChange = (event, newValue) => {
    setStagedOrganization(newValue);
  };

  const handleApply = async () => {
    const promises = [];

    // Apply role change if different
    if (stagedRole !== effectiveRole) {
      if (stagedRole === simulation.actualRole) {
        promises.push(simulation.setSimulatedRole(null));
      } else {
        promises.push(simulation.setSimulatedRole(stagedRole));
      }
    }

    // Apply organization change if different
    if (stagedOrganization?.id !== effectiveOrg?.id) {
      if (stagedOrganization?.id === simulation.actualOrganization?.id) {
        promises.push(simulation.setSimulatedOrganization(null));
      } else {
        promises.push(simulation.setSimulatedOrganization(stagedOrganization));
      }
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  };

  const handleCancel = () => {
    // Reset staged values to current effective values
    setStagedRole(effectiveRole);
    setStagedOrganization(effectiveOrg);
  };

  const isRoleSimulated = stagedRole !== simulation.actualRole;
  const isOrgSimulated = stagedOrganization?.id !== simulation.actualOrganization?.id;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1,
        backgroundColor: hasPendingChanges ? 'warning.light' : 'transparent',
        border: hasPendingChanges ? '1px solid' : 'none',
        borderColor: 'warning.main',
        borderRadius: 1,
        transition: 'all 0.3s ease',
      }}
    >
      {/* Role Selector */}
      <Tooltip 
        title={isRoleSimulated ? "Role will be simulated after clicking Apply" : "Select a role to simulate"}
        arrow
      >
        <FormControl 
          size="small" 
          sx={{ 
            minWidth: 150,
            '& .MuiOutlinedInput-root': {
              backgroundColor: isRoleSimulated ? 'warning.light' : 'background.paper',
            }
          }}
        >
          <InputLabel 
            id="role-simulator-label"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              px: 0.5,
              borderRadius: 0.5,
            }}
          >
            Role
          </InputLabel>
          <Select
            labelId="role-simulator-label"
            id="role-simulator"
            value={stagedRole ?? 0}
            onChange={handleRoleChange}
            label="Role"
            disabled={simulation.isRefreshing}
            startAdornment={
              isRoleSimulated ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  <WarningAmberIcon fontSize="small" color="warning" />
                </Box>
              ) : null
            }
          >
            <MenuItem value={0}>{ROLE_NAMES[0]}</MenuItem>
            <MenuItem value={1}>{ROLE_NAMES[1]}</MenuItem>
            <MenuItem value={2}>{ROLE_NAMES[2]}</MenuItem>
            <MenuItem value={3}>{ROLE_NAMES[3]}</MenuItem>
          </Select>
        </FormControl>
      </Tooltip>

      {/* Organization Selector */}
      <Tooltip 
        title={isOrgSimulated ? "Organization will be simulated after clicking Apply" : "Select an organization to simulate"}
        arrow
      >
        <Autocomplete
          size="small"
          open={orgDropdownOpen}
          onOpen={() => {
            setOrgDropdownOpen(true);
            setLoadingOrgs(true);
          }}
          onClose={() => {
            setOrgDropdownOpen(false);
          }}
          isOptionEqualToValue={(option, value) => value != null && option.id === value.id}
          getOptionLabel={(option) => option.name || ''}
          options={organizations}
          loading={loadingOrgs}
          disabled={simulation.isRefreshing}
          value={stagedOrganization}
          onChange={handleOrgChange}
          sx={{ 
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              backgroundColor: isOrgSimulated ? 'warning.light' : 'background.paper',
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Organization"
              InputLabelProps={{
                ...params.InputLabelProps,
                sx: {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  px: 0.5,
                  borderRadius: 0.5,
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {isOrgSimulated && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        <WarningAmberIcon fontSize="small" color="warning" />
                      </Box>
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {loadingOrgs ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Tooltip>

      {/* Apply/Cancel Buttons */}
      {hasPendingChanges && (
        <>
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={handleApply}
            disabled={simulation.isRefreshing}
            startIcon={<CheckCircleIcon />}
            sx={{ minWidth: 100 }}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={handleCancel}
            disabled={simulation.isRefreshing}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
        </>
      )}

      {/* Status indicator when simulation is active but no pending changes */}
      {!hasPendingChanges && isSimulating && (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
          <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
            Simulation Active
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default SimulationControls;
